import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useApiKeys,
  useCreateApiKey,
  useDeleteApiKey,
} from "@/hooks/useApiKeys";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate as fmtDate } from "../../lib/format";
import i18n from "../../lib/i18n";
import { Icon } from "@/components/ui/Icon";
import { SettingsEmpty, SettingsRow, SettingsSection } from "./parts";

function formatDate(iso: string | null): string {
  if (!iso) return i18n.t("common.never");
  return fmtDate(iso, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Settings → Account → API keys. Create and revoke stay small alerts on top. */
export function ApiKeysSettings() {
  const { t } = useTranslation();
  const { apiKeys, loading, refetch } = useApiKeys({ active: true });
  const createKey = useCreateApiKey();
  const deleteKey = useDeleteApiKey();

  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    const key = await createKey.mutateAsync(newKeyName.trim());
    setCreatedKey(key.key);
    setNewKeyName("");
    refetch();
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteKey.mutateAsync(deleteId);
    setDeleteId(null);
    refetch();
  };

  return (
    <>
      {loading ? (
        <SettingsEmpty loading />
      ) : apiKeys.length === 0 ? (
        <SettingsEmpty icon="key" title={t("apiKeys.empty")} hint={t("apiKeys.emptyHint")} />
      ) : (
        <SettingsSection>
          {apiKeys.map((key) => (
            <SettingsRow
              key={key.id}
              icon="key"
              label={key.name}
              subtitle={
                <>
                  <span className="font-code">{t("apiKeys.keyPrefix", { prefix: key.prefix })}</span>
                  {" · "}
                  {t("apiKeys.created", { date: formatDate(key.created_at) })}
                  {" · "}
                  {t("apiKeys.lastUsed", { date: formatDate(key.last_used_at) })}
                </>
              }
              trailing={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteId(key.id)}
                  className="text-muted hover:text-destructive"
                  title={t("apiKeys.revokeKey")}
                >
                  <Icon name="trash" size={14} />
                </Button>
              }
            />
          ))}
        </SettingsSection>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" asChild>
          <a
            href={`${import.meta.env.VITE_WEB_URL ?? "https://api.caliuapp.com"}/docs`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("apiKeys.viewApiDocs")}
          </a>
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setShowCreate(true);
            setCreatedKey(null);
            setNewKeyName("");
          }}
        >
          <Icon name="plus" size={14} />
          {t("apiKeys.newApiKey")}
        </Button>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon name="key" size={14} />
              {createdKey ? t("apiKeys.apiKeyCreated") : t("apiKeys.createApiKey")}
            </DialogTitle>
          </DialogHeader>

          {createdKey ? (
            <div className="px-4 py-4 space-y-3">
              <p className="text-sm text-ink-secondary">
                {t("apiKeys.copyHint")}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-code bg-notelist border border-border rounded-md px-3 py-2 break-all text-ink select-all">
                  {createdKey}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  title={t("apiKeys.copyToClipboard")}
                >
                  {copied ? (
                    <Icon name="check" size={14} className="text-accent" />
                  ) : (
                    <Icon name="duplicate" size={14} />
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 py-4 space-y-3">
                <Input
                  label={t("apiKeys.keyName")}
                  placeholder={t("apiKeys.keyNamePlaceholder")}
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreate();
                  }}
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreate(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={!newKeyName.trim() || createKey.isPending}
                  loading={createKey.isPending}
                >
                  {t("apiKeys.createKey")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog alert open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("apiKeys.revokeKeyTitle")}</DialogTitle>
          </DialogHeader>
          <div className="px-4 py-4">
            <p className="text-sm text-ink-secondary">
              {t("apiKeys.revokeKeyHint")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setDeleteId(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              loading={deleteKey.isPending}
            >
              {t("apiKeys.revokeKey")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
